import {
  NpiActivity,
  NpiActivityGroup,
  NpiProject,
} from "../types/npi-project.types";
import { translate } from "../../../../../translate/translate";
import type { ActivityCellStyle, ActivityRow } from "../types/npi-dashboard.types";
import { DEFAULT_ACTIVITY_STYLE, LATE_THRESHOLD_DAYS, RECENTLY_DELAYED_STYLE, SEVERELY_DELAYED_STYLE } from "../constants/dashboard.constants";

export const flattenActivityGroups = (
  groups: NpiActivityGroup[],
): NpiActivityGroup[] =>
  groups.flatMap((group) => [
    group,
    ...flattenActivityGroups(group.children ?? []),
  ]);

export const getVisibleActivities = (group: NpiActivityGroup): NpiActivity[] =>
  group.tasks.filter((activity) => activity.showInDashboard !== false);

export const getSubgroupActivities = (group: NpiActivityGroup): { subgroup: NpiActivityGroup; activity: NpiActivity }[] =>
  flattenActivityGroups(group.children ?? []).flatMap((subgroup) =>
    getVisibleActivities(subgroup).map((activity) => ({ subgroup, activity })),
  );

export const getProjectActivities = (project: NpiProject): NpiActivity[] =>
  flattenActivityGroups(project.groups ?? []).flatMap(getVisibleActivities);

export const findProjectActivity = (
  project: NpiProject,
  row: ActivityRow,
): NpiActivity | undefined =>
  (project.groups ?? [])
    .filter((group) => group.name === row.phase)
    .flatMap((group) =>
      row.subgroup
        ? getSubgroupActivities(group)
            .filter((item) => item.subgroup.name === row.subgroup)
            .map((item) => item.activity)
        : getVisibleActivities(group),
    )
    .find((activity) => activity.acronym === (row.activity ?? row.step));

export const getActivityStatusStyle = (
  activity: NpiActivity,
): ActivityCellStyle => {
  switch (activity.status) {
    case "concluido":
      return { background: "#DCFCE7", border: "#86EFAC", color: "#166534" };
    case "em_andamento":
      return { background: "#DBEAFE", border: "#93C5FD", color: "#1E40AF" };
    case "atrasado":
      return (activity.delayDays ?? 0) > LATE_THRESHOLD_DAYS
        ? SEVERELY_DELAYED_STYLE
        : RECENTLY_DELAYED_STYLE;
    case "nao_sera_executada":
      return {
        background: "#F8FAFC",
        border: "#CBD5E1",
        color: "#94A3B8",
        dashed: true,
      };
    default:
      return DEFAULT_ACTIVITY_STYLE;
  }
};

const formatDashboardDate = (date: string | null | undefined): string => {
  if (!date) return "";

  const isoMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!isoMatch) return date;

  const [, year, month, day] = isoMatch;
  const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));
  const weekday = parsedDate.toLocaleDateString("en-US", { weekday: "short" });

  return `${weekday} ${Number(month)}/${Number(day)}/${year.slice(2)}`;
};

export const getActivityDateLabel = (activity: NpiActivity): string => {
  if (activity.status === "nao_sera_executada")
    return translate.t("status_not_executed");
  if (activity.status === "nao_iniciada")
    return translate.t("nao_iniciada_label");
  return formatDashboardDate(
    activity.baselineEndDate ??
    (activity as any).baseline_end ??
    activity.executionEnd ??
    activity.baselineEnd,
  );
};
